import React, { useState } from "react";
import { Table, Switch, DatePicker, Button, message, Input } from "antd";
import dayjs from "dayjs";
import { RegisterServices } from "apis/Services/RegisterService";
import styles from "./registering.module.css";

function Subscriptions({ data, username, password, getClientSubscriptions }) {
  // Transform backend object {id: {pageData}} into array of companies
  const companies = Object.entries(data).map(([companyId, pages]) => ({
    key: companyId,
    companyId,
    companyName: pages?.companyName,
    pages,
  }));

  async function registerClientSubscriptions(companyId, allPagesData) {
    let ReqObj = {
      companyID: companyId,
    };

    // Add all page data to the request object
    allPagesData.forEach((page) => {
      const pageType = page.key.replace("Page", "").toLowerCase();

      if (pageType === "inventory") {
        ReqObj.inventoryPageSubscription = page.enabled ? "1" : "0";
        ReqObj.inventoryPageSubscriptionRegistrationDate =
          page.registrationDate || null;
        ReqObj.inventoryPageSubscriptionExpirationDate =
          page.expirationDate || null;
      } else if (pageType === "hilyte") {
        ReqObj.hilytePageSubscription = page.enabled ? "1" : "0";
        ReqObj.hilytePageSubscriptionRegistrationDate =
          page.registrationDate || null;
        ReqObj.hilytePageSubscriptionExpirationDate =
          page.expirationDate || null;

        ReqObj.hilytePageSubscriptionRemainingUploads = Number(
          page.hilytePageRemainingUploads || 0
        );

        ReqObj.hilytePageSubscriptionSubscribedUploads = Number(
          page.hilytePageSubscribedUploads || 0
        );
      }
    });

    try {
      await RegisterServices.registerClientSubscriptions(
        username,
        password,
        ReqObj
      );
      message.success(`Updated subscription status successfully!`);
    } catch (err) {
      message.error(`Something went wrong, please try again.`);
    } finally {
      getClientSubscriptions();
    }
  }

  const handleSave = (companyId, allPagesData) => {
    console.log("Saving all subscriptions:", { companyId, allPagesData });
    registerClientSubscriptions(companyId, allPagesData);
  };

  // Child component for expanded rows
  const PagesTable = ({ company }) => {
    const initialRows = Object.keys(company.pages)
      .filter((key) => key.endsWith("Page"))
      .map((key) => {
        const base = key.replace("Page", "");
        return {
          key,
          pageName: base.charAt(0).toUpperCase() + base.slice(1),
          enabled: company.pages[key] === "1" || company.pages[key] === 1,
          registrationDate: company.pages[`${base}PageRegistrationDate`] || "",
          expirationDate: company.pages[`${base}PageExpirationDate`] || "",

          hilytePageRemainingUploads:
            base === "hilyte"
              ? Number(company.pages.hilytePageRemainingUploads || 0)
              : null,

          hilytePageSubscribedUploads:
            base === "hilyte"
              ? Number(company.pages.hilytePageSubscribedUploads || 0)
              : null,
        };
      });

    const [rows, setRows] = useState(initialRows);

    const columns = [
      {
        title: "Page Name",
        dataIndex: "pageName",
        key: "pageName",
      },
      {
        title: "Enabled",
        dataIndex: "enabled",
        key: "enabled",
        render: (enabled, record) => (
          <Switch
            checked={enabled}
            onChange={(checked) => {
              setRows((prev) =>
                prev.map((row) =>
                  row.key === record.key ? { ...row, enabled: checked } : row
                )
              );
            }}
          />
        ),
      },
      {
        title: "Registration Date",
        dataIndex: "registrationDate",
        key: "registrationDate",
        render: (date, record) => (
          <DatePicker
            value={date ? dayjs(date, "MM-DD-YYYY") : null}
            format="MM-DD-YYYY"
            disabled={!record.enabled}
            onChange={(val) => {
              const newDate = val ? val.format("MM-DD-YYYY") : "";
              setRows((prev) =>
                prev.map((row) =>
                  row.key === record.key
                    ? { ...row, registrationDate: newDate }
                    : row
                )
              );
            }}
          />
        ),
      },
      {
        title: "Expiration Date",
        dataIndex: "expirationDate",
        key: "expirationDate",
        render: (date, record) => (
          <DatePicker
            value={date ? dayjs(date, "MM-DD-YYYY") : null}
            format="MM-DD-YYYY"
            disabled={!record.enabled}
            onChange={(val) => {
              const newDate = val ? val.format("MM-DD-YYYY") : "";
              setRows((prev) =>
                prev.map((row) =>
                  row.key === record.key
                    ? { ...row, expirationDate: newDate }
                    : row
                )
              );
            }}
          />
        ),
      },
      {
        title: "Remaining Uploads",
        dataIndex: "hilytePageRemainingUploads",
        key: "hilytePageRemainingUploads",
        render: (value, record) =>
          record.key === "hilytePage" ? (
            <Input
              type="number"
              min={0}
              value={value}
              disabled={!record.enabled}
              onChange={(e) => {
                const val = Number(e.target.value);
                setRows((prev) =>
                  prev.map((row) =>
                    row.key === record.key
                      ? { ...row, hilytePageRemainingUploads: val }
                      : row
                  )
                );
              }}
              style={{ width: "100%" }}
            />
          ) : (
            "-"
          ),
      },
      {
        title: "Subscribed Uploads",
        dataIndex: "hilytePageSubscribedUploads",
        key: "hilytePageSubscribedUploads",
        render: (value, record) =>
          record.key === "hilytePage" ? (
            <Input
              type="number"
              min={0}
              value={value}
              disabled={!record.enabled}
              onChange={(e) => {
                const val = Number(e.target.value);
                setRows((prev) =>
                  prev.map((row) =>
                    row.key === record.key
                      ? { ...row, hilytePageSubscribedUploads: val }
                      : row
                  )
                );
              }}
              style={{ width: "100%" }}
            />
          ) : (
            "-"
          ),
      },

      {
        title: "Actions",
        key: "actions",
        render: (_, record, index) =>
          index === 0 ? (
            <Button
              type="primary"
              onClick={() => handleSave(company.companyId, rows)}
            >
              Save All
            </Button>
          ) : null,
      },
    ];

    return (
      <Table
        columns={columns}
        bordered
        dataSource={rows}
        pagination={false}
        className={styles.subscriptionsDetailsTable}
        scroll={{ x: 1100 }}
      />
    );
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
    },
  ];

  return (
    <div className={styles.subscriptionsSection}>
      <div className={styles.sectionHeader}>
        <h3 className="fw-bold mb-0">Subscriptions</h3>
      </div>

      <Table
        columns={columns}
        expandable={{
          expandedRowRender: (company) => <PagesTable company={company} />,
          expandRowByClick: true,
          rowExpandable: () => true,
        }}
        dataSource={companies}
        pagination={false}
        className={styles.subscriptionsTable}
        scroll={{ x: 480 }}
      />
    </div>
  );
}

export default Subscriptions;
